/**
 * Calculate the difference between two dates.
 *
 * @param {Date} first - The earlier date.
 * @param {Date} today - The later date.
 * @returns {Object} The difference between the dates.
 * @property {number} milliseconds - The difference in milliseconds.
 * @property {number} seconds - The remaining seconds.
 * @property {number} minutes - The remaining minutes.
 * @property {number} hours - The remaining hours, capped at zero.
 * @property {number} days - The elapsed full days.
 * @property {number} months - The calendar-month difference.
 * @property {number} years - The calendar-year difference.
 */
export const datediff = (first, today) => {
    const isValidDate = first instanceof Date && today instanceof Date;
    if (!isValidDate) {
        return {};
    }
    const milliseconds = Math.round(today - first);
    const minutes = Math.round(((milliseconds % 86400000) % 3600000) / 60000);
    const hours = Math.floor((milliseconds % 86400000) / 3600000);
    const months =
        today.getMonth() - first.getMonth() + 12 * (today.getFullYear() - first.getFullYear());
    const days = (today - first) / (1000 * 60 * 60 * 24);
    const years = today.getFullYear() - first.getFullYear();

    return {
        milliseconds,
        minutes,
        hours: hours < 0 ? 0 : hours,
        seconds: Math.round((((milliseconds % 86400000) % 3600000) % 60000) / 1000),
        /**
         * days is considered to be 0 as soon as a diff
         * between the dates is less than 24 hours
         */
        days: days < 1 ? 0 : Math.floor(days),
        months,
        years,
    };
};
/**
 * Get a text representation of the time between two dates.
 *
 * @param {Date} firstDate - The earlier date.
 * @param {Date} secondDate - The later date.
 * @param {Object} [config={}] - Text formatting options.
 * @param {string} [config.prefix=''] - Text to prepend to the result.
 * @param {string} [config.daysLongText='Tage'] - Label used for multiple days.
 * @returns {Object} The formatted time difference.
 * @property {string} days - The formatted day difference, or an empty string.
 * @property {string} hours - The formatted hour difference, or an empty string.
 * @property {string} minutes - The formatted minute difference, or a just-now label.
 * @property {boolean} isExpired - Whether the difference contains no full time unit.
 * @property {string} justNow - The just-now label, or an empty string.
 */
export const getTimeDiffRepresentation = (firstDate, secondDate, config = {}) => {
    const justNow = 'Gerade eben';
    const { hours, days, minutes } = datediff(firstDate, secondDate);
    const { prefix = '', daysLongText = 'Tage' } = config;

    const minutesAbs = Math.max(minutes, 0);
    const hoursAbs = Math.max(hours, 0);
    const daysAbs = Math.max(days, 0);

    const getDays = days => {
        if (days === 1) return `${prefix} 1 Tag`;
        return `${prefix} ${daysAbs} ${daysLongText}`;
    };

    const getHours = hours => {
        if (hours === 1) return `${prefix} 1 Stunde`;
        return `${prefix} ${hours} Stunden`;
    };

    const getMinutesText = minutes => {
        if (minutes === 1) return `${prefix} 1 Minute`;
        if (minutes < 1) return justNow;
        return `${prefix} ${minutes} Minuten`;
    };

    const daysLeft = !daysAbs ? '' : getDays(daysAbs).trim();
    const hoursLeft = !hoursAbs ? '' : getHours(hoursAbs).trim();
    const minutesLeft = !hoursLeft && !daysLeft ? getMinutesText(minutes).trim() : '';
    const isExpired = !minutesAbs && !hoursAbs && !daysAbs;

    return {
        days: daysLeft,
        hours: hoursLeft,
        minutes: minutesLeft,
        isExpired,
        justNow: minutesLeft === justNow ? justNow : '',
    };
};
